import { ChevronDown, type LucideIcon, X } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";

export function SearchSelect({
	value,
	onChange,
	options,
	placeholder = "Select...",
	searchPlaceholder = "Search...",
	icons,
	leadingVisual,
	clearable = true,
}: {
	value: string;
	onChange: (v: string) => void;
	options: readonly string[];
	placeholder?: string;
	searchPlaceholder?: string;
	icons?: Record<string, LucideIcon>;
	leadingVisual?: (option: string) => ReactNode;
	clearable?: boolean;
}) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const filtered = options.filter((o) =>
		o.toLowerCase().includes(search.toLowerCase()),
	);
	const renderLeadingVisual = (option: string) => {
		if (leadingVisual) return leadingVisual(option);
		if (!icons?.[option]) return null;
		const Icon = icons[option];
		return <Icon size={14} className="shrink-0" />;
	};

	useEffect(() => {
		const handler = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setOpen(false);
				setSearch("");
			}
		};
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, []);

	return (
		<div ref={containerRef} className="relative">
			<button
				type="button"
				onClick={() => {
					setOpen((o) => !o);
					setTimeout(() => inputRef.current?.focus(), 0);
				}}
				className="w-full flex items-center justify-between border border-border bg-background px-3 py-2 text-base outline-none focus:outline-none focus:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
			>
				<span
					className={`flex items-center gap-1.5 ${value ? "text-foreground" : "text-muted-foreground"}`}
				>
					{value ? renderLeadingVisual(value) : null}
					{value || placeholder}
				</span>
				<div className="flex items-center gap-1">
					<ChevronDown
						size={14}
						className={`text-muted-foreground ${clearable && value ? "mr-5" : ""}`}
					/>
				</div>
			</button>
			{clearable && value && (
				<button
					type="button"
					onClick={() => onChange("")}
					className="absolute right-8 top-1/2 inline-flex size-5 -translate-y-1/2 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
					aria-label="Clear selection"
				>
					<X size={14} />
				</button>
			)}
			{open && (
				<div className="absolute z-50 mt-1 w-full border border-border bg-background shadow-lg">
					<div className="p-2">
						<input
							ref={inputRef}
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder={searchPlaceholder}
							className="w-full border border-border bg-background px-3 py-1.5 text-base outline-none focus:border-brand"
						/>
					</div>
					<ul className="max-h-52 overflow-y-auto pb-1">
						{filtered.length === 0 ? (
							<li className="px-3 py-2 text-base text-muted-foreground">
								No results
							</li>
						) : (
							filtered.map((option) => (
								<li
									key={option}
									onMouseDown={() => {
										onChange(option);
										setOpen(false);
										setSearch("");
									}}
									className={`cursor-pointer px-3 py-2 text-base hover:bg-accent flex items-center gap-1.5 ${value === option ? "text-brand" : ""}`}
								>
									{renderLeadingVisual(option)}
									{option}
								</li>
							))
						)}
					</ul>
				</div>
			)}
		</div>
	);
}
